import React, { useEffect, useState, useCallback, useId } from "react";

import Input from "src/components/Input/Input";

import "./StopWatch.scss";

interface IProps {
  id?: string;
  label?: string;
  onChange(time: string): void;
  time: string;
  enableMinutesInput?: boolean;
  enableSecondsInput?: boolean;
  enableMillisecondsInput?: boolean;
}

const convertTimeToMinutes = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = time - minutes * 60;
  const thousands = Math.round((seconds - Math.floor(seconds)) * 100);

  const formatNumber = (num: number) => num.toString().padStart(2, "0");

  return `${formatNumber(minutes)}:${formatNumber(Math.floor(seconds))}:${formatNumber(thousands)}`;
}

const convertTimeToSeconds = (time: string) => {
  const timeSplit = time.split(":");
  let timeInSeconds = 0;

  if (timeSplit.length === 3) {
    if (Number(timeSplit[0]) > 0) {
      const secondsToAdd = Number(timeSplit[0]) * 60;
      timeInSeconds += secondsToAdd;
    }
    timeInSeconds += Number(timeSplit[1]);
  }

  return timeInSeconds;
}

const secondsToTime = (seconds: number) =>{
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  const formattedMins = mins.toString().padStart(2, "0");
  const formattedSecs = secs.toString().padStart(2, "0");

  return `${formattedMins}:${formattedSecs}`;
}

const StopWatch: React.FC<IProps> = (props) => {
  const {
    id,
    label,
    onChange,
    time,
    enableMinutesInput = true,
    enableSecondsInput = true,
    enableMillisecondsInput = true
  } = props;

  const [minutes, setMinutes] = useState<string>("");
  const [seconds, setSeconds] = useState<string>("");
  const [milliseconds, setMilliseconds] = useState<string>("");

  // Generate unique IDs
  const uniqueId = useId();
  const instanceId = id || `stopwatch-${uniqueId}`;

  const minId = `${instanceId}-minutes`;
  const secId = `${instanceId}-seconds`;
  const msId = `${instanceId}-milliseconds`;

  // Parse time string and update state
  useEffect(() => {
    const [min = "", sec = "", ms = ""] = time.split(":");
    // if (enableMinutesInput) setMinutes(min || "00");
    if (enableMinutesInput) setMinutes(min);
    if (enableSecondsInput) setSeconds(sec);
    if (enableMillisecondsInput) setMilliseconds(ms);
  }, [time, enableMinutesInput, enableSecondsInput, enableMillisecondsInput]);

  // Handle time change and call parent's onChange
  const handleTimeChange = useCallback((
    newMinutes: string,
    newSeconds: string,
    newMilliseconds: string
  ) => {
    const finalMinutes = enableMinutesInput ? newMinutes : "00";
    const finalSeconds = enableSecondsInput ? newSeconds : "00";
    const finalMilliseconds = enableMillisecondsInput ? newMilliseconds : "00";

    onChange(`${finalMinutes}:${finalSeconds}:${finalMilliseconds}`);
  }, [onChange, enableMinutesInput, enableSecondsInput, enableMillisecondsInput]);

  // Handle minutes change
  const handleMinutesChange = useCallback((value: string) => {
    setMinutes(value);
    handleTimeChange(value, seconds, milliseconds);
  }, [seconds, milliseconds, handleTimeChange]);

  // Handle seconds change
  const handleSecondsChange = useCallback((value: string) => {
    setSeconds(value);
    handleTimeChange(minutes, value, milliseconds);
  }, [minutes, milliseconds, handleTimeChange]);

  // Handle milliseconds change
  const handleMillisecondsChange = useCallback((value: string) => {
    setMilliseconds(value);
    handleTimeChange(minutes, seconds, value);
  }, [minutes, seconds, handleTimeChange]);

  // Auto-navigate between inputs
  const goToNextInput = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const { id: targetId, value } = e.target as HTMLInputElement;

    const nonNavigationKeys = [
      "Backspace",
      "Tab",
      "ShiftLeft",
      "ShiftRight",
      "ControlLeft",
      "ControlRight",
      "KeyA"
    ];

    if (nonNavigationKeys.includes(e.code) || isNaN(Number(value)) || value.length < 2) {
      return;
    }

    const inputOrder = [minId, secId, msId].filter(id => {
      if (id === minId && !enableMinutesInput) return false;
      if (id === secId && !enableSecondsInput) return false;
      if (id === msId && !enableMillisecondsInput) return false;
      return true;
    });

    const currentIndex = inputOrder.indexOf(targetId);
    if (currentIndex !== -1 && currentIndex < inputOrder.length - 1) {
      const nextInput = document.getElementById(inputOrder[currentIndex + 1]);
      if (nextInput) {
        nextInput.focus();
        (nextInput as HTMLInputElement).select();
      }
    }
  }, [minId, secId, msId, enableMinutesInput, enableSecondsInput, enableMillisecondsInput]);

  // Get visible inputs count for styling
  const visibleInputsCount = [
    enableMinutesInput,
    enableSecondsInput,
    enableMillisecondsInput
  ].filter(Boolean).length;

  const containerClass = `StopWatch reason qualified stopwatch-inputs-${visibleInputsCount}`;

  return (
    <div className={containerClass}>
      {enableMinutesInput && (
        <Input
          label={label ? `${label} (mm)` : "Minutes (mm)"}
          id={minId}
          type="text"
          value={minutes}
          onChange={(e) => {
            e.stopPropagation();
            handleMinutesChange(e.target.value);
          }}
          placeholder="mm"
          maxLength={2}
          onKeyUp={goToNextInput}
        />
      )}

      {enableSecondsInput && (
        <Input
          label={label ? `${label} (ss)` : "Seconds (ss)"}
          id={secId}
          type="text"
          value={seconds}
          onChange={(e) => {
            e.stopPropagation();
            handleSecondsChange(e.target.value);
          }}
          placeholder="ss"
          maxLength={2}
          onKeyUp={goToNextInput}
        />
      )}

      {enableMillisecondsInput && (
        <Input
          label={label ? `${label} (tt)` : "Milliseconds (tt)"}
          id={msId}
          type="text"
          value={milliseconds}
          onChange={(e) => {
            e.stopPropagation();
            handleMillisecondsChange(e.target.value);
          }}
          placeholder="tt"
          maxLength={2}
          onKeyUp={goToNextInput}
        />
      )}
    </div>
  );
};

export { StopWatch, convertTimeToMinutes, convertTimeToSeconds, secondsToTime };
